import { guestMpcCreate, guestMpcRefresh, isInsufficientScopeError } from "./magicLinkApi";
import { generateMpcWalletUserShare } from "../utils/dwclient/portalhqutils";
/**
 * Background creation of the guest's built-in Portal MPC wallet.
 * Best-effort: chat/call access does not depend on it, so failures only log.
 * The user share is kept locally; the client id is remembered orchestrator-side
 * per email so a repeat invite reuses the same client.
 */
const LS_CLIENT_ID = "dw_guest_mpc_client_id";
const LS_SHARE = "dw_guest_mpc_share";
const LS_ADDRESS = "dw_guest_mpc_address";
const LS_OWNER = "dw_guest_mpc_owner";
export function getGuestMpcAddress() {
    return localStorage.getItem(LS_ADDRESS);
}
/** Owner tag binds locally cached MPC material to the guest identity, so a
 *  different guest on a shared browser never reuses someone else's shares. */
function ownerTag(sessionJwt) {
    try {
        const payload = JSON.parse(atob(sessionJwt.split(".")[1]));
        return `${payload.email || ""}|${payload.room || ""}`.toLowerCase();
    }
    catch {
        return null;
    }
}
/** Scope claim from the guest session JWT ('chat' | 'full'; missing = 'chat'). */
function tokenScope(sessionJwt) {
    try {
        const payload = JSON.parse(atob(sessionJwt.split(".")[1]));
        return payload.scope === "full" ? "full" : "chat";
    }
    catch {
        return "chat";
    }
}
export async function ensureGuestMpcWallet(sessionJwt) {
    try {
        // Chat-only invites don't include an MPC wallet — the orchestrator would
        // reject /mpc/* with 403 insufficient_scope, so don't even try.
        if (tokenScope(sessionJwt) !== "full") {
            console.info("[GuestMPC] Chat-only invite — skipping built-in wallet provisioning.");
            return;
        }
        const owner = ownerTag(sessionJwt);
        const cachedOwner = localStorage.getItem(LS_OWNER);
        if (cachedOwner && cachedOwner !== owner) {
            clearGuestMpcWallet(); // different guest on this browser — drop stale material
        }
        if (localStorage.getItem(LS_SHARE) &&
            localStorage.getItem(LS_ADDRESS) &&
            localStorage.getItem(LS_OWNER) === owner) {
            return; // already provisioned on this device for this guest
        }
        if (owner)
            localStorage.setItem(LS_OWNER, owner);
        const { clientId } = await guestMpcCreate(sessionJwt);
        localStorage.setItem(LS_CLIENT_ID, clientId);
        const { clientSessionToken } = await guestMpcRefresh(sessionJwt, clientId);
        if (!clientSessionToken)
            throw new Error("no clientSessionToken");
        const shares = await generateMpcWalletUserShare(clientSessionToken);
        const secp = shares?.secp256k1 || shares?.SECP256K1;
        const address = secp?.share?.address || secp?.address || null;
        localStorage.setItem(LS_SHARE, JSON.stringify(shares));
        if (address)
            localStorage.setItem(LS_ADDRESS, address);
        console.log("[GuestMPC] Built-in MPC wallet ready", address ? `(${address})` : "");
    }
    catch (e) {
        if (isInsufficientScopeError(e)) {
            // Server-side scope enforcement: this invite only includes chat access.
            console.info("[GuestMPC] Invite only includes chat access — no built-in wallet for this guest.");
            return;
        }
        console.warn("[GuestMPC] Background wallet creation failed (non-blocking):", e?.message || e);
    }
}
export function clearGuestMpcWallet() {
    localStorage.removeItem(LS_CLIENT_ID);
    localStorage.removeItem(LS_SHARE);
    localStorage.removeItem(LS_ADDRESS);
    localStorage.removeItem(LS_OWNER);
}
